import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Quote,
  Receipt,
  Banknote,
  ArrowDownRight,
  Plus,
  ReceiptText,
  Printer,
  Wallet,
  Landmark,
  ArrowDownLeft,
} from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { StatCard } from "../components/ui/StatCard";
import { PageHeader } from "../components/ui/PageHeader";
import { DataTable, type Column } from "../components/ui/DataTable";
import { TableExportToolbar } from "../components/TableExportToolbar";
import { PrintTable } from "../components/PrintTable";
import { RapportenPanel } from "../components/reports/RapportenPanel";
import { eur, fmtDate, fmtTime, nlStatus } from "../lib/format";
import api from "../lib/api";
import { printRowsAsPdf } from "../lib/printTable";
import { useAuth } from "../lib/auth";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type UnifiedRow = {
  type: "Offerte" | "Factuur" | "Betaling" | "Uitgave";
  number: string;
  date: string; // ISO
  party: string;
  gross: number;
  vat: number;
  net: number;
  vatType: string;
  status: string;
  rawStatus: string;
};

const TYPE_BADGE: Record<UnifiedRow["type"], string> = {
  Offerte: "bg-indigo-50/80 text-indigo-700 border-indigo-200",
  Factuur: "bg-blue-50/80 text-blue-700 border-blue-200",
  Betaling: "bg-emerald-50/80 text-emerald-700 border-emerald-200",
  Uitgave: "bg-amber-50/80 text-amber-700 border-amber-200",
};

const joinVatTypes = (lines: any[]): string => {
  const set = new Set<number>();
  (lines || []).forEach((l: any) => {
    if (l.vatRate?.percentage !== undefined && l.vatRate?.percentage !== null) {
      set.add(Number(l.vatRate.percentage));
    }
  });
  if (!set.size) return "—";
  return [...set].map((p) => `${p}%`).join(", ");
};

const dashboardColumns: Column<UnifiedRow>[] = [
  { key: "type", header: "Type", accessor: (r) => r.type,
    render: (r) => <Badge className={TYPE_BADGE[r.type]}>{r.type}</Badge>,
    sortValue: (r) => r.type },
  { key: "number", header: "Nummer", accessor: (r) => r.number,
    render: (r) => <span className="font-semibold text-slate-800">{r.number}</span> },
  { key: "date", header: "Datum", accessor: (r) => r.date ? fmtDate(r.date) : "—",
    sortValue: (r) => r.date ? new Date(r.date).getTime() : 0 },
  { key: "time", header: "Tijd", accessor: (r) => r.date ? fmtTime(r.date) : "—",
    sortValue: (r) => r.date ? new Date(r.date).getTime() : 0, sortable: false },
  { key: "party", header: "Klant / leverancier", accessor: (r) => r.party },
  { key: "gross", header: "Bruto", accessor: (r) => eur(r.gross), className: "text-right",
    sortValue: (r) => r.gross },
  { key: "vat", header: "BTW", accessor: (r) => r.vat ? eur(r.vat) : "—", className: "text-right",
    sortValue: (r) => r.vat },
  { key: "net", header: "Netto", accessor: (r) => r.net ? eur(r.net) : "—", className: "text-right",
    sortValue: (r) => r.net },
  { key: "vatType", header: "BTW-soort", accessor: (r) => r.vatType },
  { key: "status", header: "Status", accessor: (r) => r.status,
    render: (r) => (r.rawStatus ? <Badge status={r.rawStatus}>{r.status}</Badge> : <span className="text-slate-400">—</span>) },
];

export default function Dashboard() {
  const { features, activeCompanyId } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [recent, setRecent] = useState<any[]>([]);
  const [unified, setUnified] = useState<UnifiedRow[]>([]);
  const [visibleRows, setVisibleRows] = useState<UnifiedRow[]>([]);
  const [selected, setSelected] = useState<UnifiedRow[]>([]);
  const [period, setPeriod] = useState<"week" | "month" | "year">("month");
  const [chartType, setChartType] = useState<"bar" | "line" | "donut">("bar");
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [tab, setTab] = useState<"overzicht" | "rapporten">("overzicht");

  useEffect(() => {
    api.get("/api/dashboard").then((res) => {
      setStats(res.data);
      setRecent(res.data.recentInvoices || []);
    });
    // Staafdiagram-data (omzet + uitgaven) per geselecteerde periode.
    setChartLoading(true);
    api
      .get("/api/dashboard/revenue", { params: { period } })
      .then((res) => setChartData(res.data?.buckets || []))
      .finally(() => setChartLoading(false));
    // Haal de entiteitlijsten op die in het actieve pakket zitten en merge
    // ze client-side tot één overzicht. Voor Budget Factuur is dat alleen
    // facturen (geen offertes/uitgaven/inkomsten).
    Promise.all([
      features.quotes ? api.get("/api/quotes") : Promise.resolve({ data: [] }),
      api.get("/api/invoices"),
      features.payments ? api.get("/api/payments") : Promise.resolve({ data: [] }),
      features.expenses ? api.get("/api/expenses") : Promise.resolve({ data: [] }),
    ]).then(([q, i, p, e]) => {
      const rows: UnifiedRow[] = [
        ...(q.data || []).map((x: any): UnifiedRow => ({
          type: "Offerte",
          number: x.number || "—",
          date: x.issueDate || x.createdAt || "",
          party: x.contact?.companyName || x.contact?.attentionTo || x.contact?.name || "—",
          gross: Number(x.total) || 0,
          vat: Number(x.vatTotal) || 0,
          net: Number(x.subtotal) || 0,
          vatType: joinVatTypes(x.lines),
          status: nlStatus(x.status),
          rawStatus: x.status,
        })),
        ...(i.data || []).map((x: any): UnifiedRow => ({
          type: "Factuur",
          number: x.number || "—",
          date: x.issueDate || x.createdAt || "",
          party: x.contact?.companyName || x.contact?.attentionTo || x.contact?.name || "—",
          gross: Number(x.total) || 0,
          vat: Number(x.vatTotal) || 0,
          net: Number(x.subtotal) || 0,
          vatType: joinVatTypes(x.lines),
          status: nlStatus(x.status),
          rawStatus: x.status,
        })),
        ...(p.data || []).map((x: any): UnifiedRow => {
          const isRefund = x.type === "REFUND";
          const sign = isRefund ? -1 : 1;
          return {
            type: "Betaling",
            number: x.reference || x.invoice?.number || "—",
            date: x.date || x.createdAt || "",
            party: x.contact?.name || x.invoice?.contact?.companyName || "—",
            gross: sign * (Number(x.invoice?.total ?? x.amount) || 0),
            vat: sign * (Number(x.invoice?.vatTotal ?? 0) || 0),
            net: sign * (Number(x.invoice?.subtotal ?? 0) || 0),
            vatType: joinVatTypes(x.invoice?.lines),
            status: isRefund ? "Storno" : nlStatus(x.invoice?.status),
            rawStatus: isRefund ? "" : x.invoice?.status || "",
          };
        }),
        ...(e.data || []).map((x: any): UnifiedRow => ({
          type: "Uitgave",
          number: "—",
          date: x.date || x.createdAt || "",
          party: x.merchant || x.contact?.name || x.contact?.companyName || "—",
          gross: Number(x.amountInclVat) || 0,
          vat: Number(x.vatAmount) || 0,
          net: Number(x.amountExclVat != null ? x.amountExclVat : (Number(x.amountInclVat) - Number(x.vatAmount || 0))) || 0,
          vatType: x.vatRate?.percentage != null ? `${Number(x.vatRate.percentage)}%` : "—",
          status: nlStatus(x.ocrStatus),
          rawStatus: x.ocrStatus || "",
        })),
      ];
      setUnified(rows);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.quotes, features.expenses, features.payments, activeCompanyId]);

  // Herlaad staafdiagram-data bij wissel van periode-filter.
  useEffect(() => {
    setChartLoading(true);
    api
      .get("/api/dashboard/revenue", { params: { period } })
      .then((res) => setChartData(res.data?.buckets || []))
      .finally(() => setChartLoading(false));
  }, [period]);

  const formatCurrency = (n: number) => `€ ${Number(n || 0).toFixed(2)}`;

  // Donut-data: totale verkoop (omzet) vs. inkoop (uitgaven) over de gekozen
  // periode, samengevat uit de staafdiagram-buckets.
  const donutData = [
    {
      name: "Verkoop",
      value: Math.round((chartData.reduce((s, d) => s + Number(d.revenue || 0), 0) + Number.EPSILON) * 100) / 100,
      fill: "#10b981",
    },
    {
      name: "Inkoop",
      value: Math.round((chartData.reduce((s, d) => s + Number(d.expenses || 0), 0) + Number.EPSILON) * 100) / 100,
      fill: "#f43f5e",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Dashboard"
        subtitle="Overzicht van je bedrijfsadministratie"
        action={
          <div className="flex gap-3">
            {features.expenses && (
              <Link to="/expenses">
                <Button variant="secondary" size="sm">
                  <ReceiptText size={27} /> Nieuwe uitgave
                </Button>
              </Link>
            )}
            <Link to="/invoices">
              <Button size="sm">
                <Plus size={27} /> Nieuwe factuur
              </Button>
            </Link>
          </div>
        }
      />

      {/* Tabs: standaard overzicht vs. het totaalplaatje met rapporten. */}
      <div className="flex gap-2">
        {(
          [
            { key: "overzicht", label: "Overzicht" },
            { key: "rapporten", label: "Rapporten" },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-brand-500 text-white shadow-lg shadow-brand-500/25" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overzicht" && (
      <>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="Openstaande facturen"
          value={stats ? formatCurrency(stats.outstandingAmount) : "—"}
          icon={FileText}
          color="amber"
          delay={0}
        />
        {features.quotes && (
          <StatCard
            label="Offertes deze maand"
            value={stats?.monthlyQuoteCount ?? "—"}
            icon={Quote}
            color="indigo"
            delay={0.08}
          />
        )}
        {features.expenses && (
          <StatCard
            label="Uitgaven deze maand"
            value={stats ? formatCurrency(stats.monthlyExpenseTotal) : "—"}
            icon={Receipt}
            color="rose"
            delay={0.16}
          />
        )}
        {features.payments && (
          <StatCard
            label="Ontvangen betalingen"
            value={stats ? formatCurrency(stats.monthlyPaymentTotal) : "—"}
            icon={Banknote}
            color="emerald"
            delay={0.24}
          />
        )}
      </div>

      {/* Kasboek-statistieken (alleen wanneer de kasboek-module aanstaat). */}
      {features.kasboek && (
        <div className="grid md:grid-cols-3 gap-6">
          <StatCard
            label="Kas-omzet deze maand"
            value={stats ? formatCurrency(stats.monthlyCashIn) : "—"}
            icon={ArrowDownLeft}
            color="emerald"
            delay={0}
          />
          <StatCard
            label="Kas-uitgaven deze maand"
            value={stats ? formatCurrency(stats.monthlyCashOut) : "—"}
            icon={Wallet}
            color="rose"
            delay={0.08}
          />
          <StatCard
            label="Kassaldo"
            value={stats ? formatCurrency(stats.cashSaldo) : "—"}
            icon={Landmark}
            color="indigo"
            delay={0.16}
          />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card
          className="lg:col-span-2 p-6"
          title="Omzet & uitgaven"
          subtitle="Verkoop vs. inkoop per week, maand of jaar"
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                {(["week", "month", "year"] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                      period === p ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {p === "week" ? "Week" : p === "month" ? "Maand" : "Jaar"}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                {([
                  { k: "bar", label: "Staaf" },
                  { k: "line", label: "Lijn" },
                  { k: "donut", label: "Donut" },
                ] as const).map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setChartType(t.k)}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                      chartType === t.k ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          }
        >
          <div className="h-72 relative">
            {chartLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">Laden…</div>
            )}
            {chartType === "donut" ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    contentStyle={{
                      borderRadius: "1rem",
                      border: "1px solid rgba(226,232,240,0.8)",
                      boxShadow: "0 10px 30px -10px rgba(15,23,42,0.15)",
                    }}
                    formatter={(value: any) => `€ ${Number(value).toFixed(2)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {donutData.map((d, i) => (
                      <Cell key={i} fill={d.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : chartType === "line" ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "1rem",
                      border: "1px solid rgba(226,232,240,0.8)",
                      boxShadow: "0 10px 30px -10px rgba(15,23,42,0.15)",
                    }}
                    formatter={(value: any) => `€ ${Number(value).toFixed(2)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Line type="monotone" dataKey="revenue" name="Verkoop" stroke="#10b981" strokeWidth={3} dot={false} />
                  <Line type="monotone" dataKey="expenses" name="Inkoop" stroke="#f43f5e" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(16,185,129,0.06)" }}
                    contentStyle={{
                      borderRadius: "1rem",
                      border: "1px solid rgba(226,232,240,0.8)",
                      boxShadow: "0 10px 30px -10px rgba(15,23,42,0.15)",
                    }}
                    formatter={(value: any) => `€ ${Number(value).toFixed(2)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Bar dataKey="revenue" name="Verkoop" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Inkoop" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-6" title="Recente facturen" action={
          <Link to="/invoices" className="text-sm font-semibold text-brand-600 hover:text-brand-700">
            Bekijk alles
          </Link>
        }>
          <div className="space-y-3">
            {recent.length === 0 && (
              <p className="text-sm text-slate-500">Nog geen facturen.</p>
            )}
            {recent.map((inv, i) => (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/invoices?id=${inv.id}`}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50/80 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-100 transition-all"
                >
                  <div>
                    <p className="text-sm font-bold text-slate-800">{inv.invoiceNumber || inv.number}</p>
                    <p className="text-xs text-slate-500">{inv.contact?.name || "Geen contact"}</p>
                    <p className="text-[11px] text-slate-400">{inv.issueDate ? fmtDate(inv.issueDate) : "—"}</p>
                  </div>
                  <p className="font-bold text-sm">€ {Number(inv.total || 0).toFixed(2)}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </Card>
      </div>

      <Card
        className="p-6"
        title="Gecombineerd overzicht"
        subtitle="Offertes, facturen, betalingen en uitgaven in één tabel"
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const exportRows = selected.length ? selected : visibleRows;
              if (!exportRows.length) {
                alert("Er zijn geen rijen om af te drukken.");
                return;
              }
              printRowsAsPdf(
                selected.length ? "Gecombineerd overzicht (selectie)" : "Gecombineerd overzicht",
                exportRows,
                dashboardColumns
              );
            }}
            title="Print het overzicht (zichtbare / geselecteerde rijen) als PDF"
          >
            <Printer size={18} /> Overzicht afdrukken
          </Button>
        }
      >
        <DataTable
          rows={unified}
          columns={dashboardColumns}
          rowKey={(r) => r.type + ":" + r.number + ":" + r.date}
          searchPlaceholder="Zoek in overzicht..."
          storageKey="dashboard-overzicht"
          initialSort={{ key: "date", dir: "desc" }}
          emptyMessage="Nog geen boekingen."
          onVisibleRowsChange={setVisibleRows}
          selectable
          onSelectionChange={setSelected}
          toolbar={
            <TableExportToolbar
              rows={selected.length ? selected : visibleRows}
              columns={dashboardColumns}
              filename={selected.length ? "overzicht-selectie" : "overzicht"}
              source="overzicht"
              printTitle={selected.length ? "Gecombineerd overzicht (selectie)" : "Gecombineerd overzicht"}
            />
          }
        />
        <PrintTable rows={selected.length ? selected : visibleRows} columns={dashboardColumns} title="Gecombineerd overzicht" />
      </Card>

      {stats?.overdueInvoices > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200 text-amber-800 text-sm font-medium flex items-center gap-3"
        >
          <ArrowDownRight size={27} />
          Er zijn {stats.overdueInvoices} achterstallige facturen.
          <Link to="/invoices" className="ml-auto underline">Bekijk</Link>
        </motion.div>
      )}
      </>
      )}

      {tab === "rapporten" && <RapportenPanel kasboek={features.kasboek} />}
    </div>
  );
}
